import type { ReactNode } from 'react'
import type { StockRow } from '@/lib/market/types'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { ChangeBadge, ChangeText } from '@/components/market/change'
import { useLive, useQuote, usePriceFlash } from '@/components/market/live-provider'
import { useNow } from '@/hooks/use-mounted'
import { formatDay, formatEatClock } from '@/lib/market/calendar'
import { formatCompact, formatKes, formatNumber, formatPrice } from '@/lib/market/format'
import CompanyMark from '@/components/market/company-mark'
import WatchButton from '@/components/market/watch-button'

interface StockHeaderProps {
    stock: StockRow
    sessionDay: number
}

const StockHeader = ({ stock, sessionDay }: StockHeaderProps): ReactNode => {
    const quote = useQuote(stock.live)
    const flash = usePriceFlash(quote.last)
    const { phase } = useLive()
    const now = useNow(1000)
    const open = phase === 'open'

    const stats = [
        { label: 'Open', value: formatPrice(quote.open) },
        { label: 'High', value: formatPrice(quote.high) },
        { label: 'Low', value: formatPrice(quote.low) },
        { label: 'Prev close', value: formatPrice(quote.prevClose) },
        { label: 'VWAP', value: formatPrice(quote.vwap) },
        { label: 'Volume', value: formatCompact(quote.volume) },
        { label: 'Turnover', value: formatKes(quote.turnover) },
        { label: 'Deals', value: formatNumber(quote.deals) }
    ]

    return (
        <header className='mb-6'>
            <nav aria-label='Breadcrumb' className='mb-4 text-xs text-muted-foreground'>
                <ol className='flex flex-wrap items-center gap-1.5'>
                    <li><Link href='/equities' className='hover:text-foreground'>Equities</Link></li>
                    {stock.sectorSlug && stock.sectorName && stock.sectorId && (
                        <>
                            <li aria-hidden='true'>/</li>
                            <li>
                                <Link href={`/equities?sector=${stock.sectorSlug}`} className='hover:text-foreground'>
                                    <span data-mdlfy-cms={`sectors:${stock.sectorId}:name`}>{stock.sectorName}</span>
                                </Link>
                            </li>
                        </>
                    )}
                    <li aria-hidden='true'>/</li>
                    <li aria-current='page' className='font-mono text-foreground'>{stock.ticker}</li>
                </ol>
            </nav>

            <div className='flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between'>
                <div className='flex min-w-0 items-start gap-4'>
                    <CompanyMark ticker={stock.ticker} size='lg' />

                    <div className='min-w-0'>
                        <h1 className='font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl'>
                            <span data-mdlfy-cms={`companies:${stock.id}:name`}>{stock.name}</span>
                        </h1>

                        <div className='mt-2 flex flex-wrap items-center gap-2 text-xs'>
                            <span className='rounded-md bg-primary/12 px-1.5 py-0.5 font-mono font-semibold text-primary'>{stock.ticker}</span>
                            {stock.segment && (
                                <span className='rounded-md border border-border px-1.5 py-0.5 text-muted-foreground'>
                                    <span data-mdlfy-cms={`companies:${stock.id}:segment`}>{stock.segment}</span>
                                </span>
                            )}
                            {stock.inK20 && <span className='rounded-md border border-border px-1.5 py-0.5 text-muted-foreground'>Kilele 20</span>}
                            {stock.inK25 && <span className='rounded-md border border-border px-1.5 py-0.5 text-muted-foreground'>Kilele 25</span>}
                            <WatchButton id={stock.id} ticker={stock.ticker} variant='button' className='h-7 px-2 text-xs' />
                        </div>
                    </div>
                </div>

                <div className='shrink-0 lg:text-right'>
                    <div className='flex flex-wrap items-baseline gap-3 lg:justify-end'>
                        <span className='text-sm text-muted-foreground'>KES</span>
                        <span className={cn('rounded-md px-1 text-4xl font-semibold tracking-tight text-foreground', flash)}>{formatPrice(quote.last)}</span>
                    </div>

                    <div className='mt-1.5 flex flex-wrap items-center gap-2 lg:justify-end'>
                        <ChangeText value={quote.change} percent={false} className='text-sm font-medium' />
                        <ChangeBadge value={quote.changePct} />
                    </div>

                    <p className='mt-1.5 text-xs text-muted-foreground'>
                        {open
                            ? <span className='inline-flex items-center gap-1.5'><span aria-hidden='true' className='live-dot size-1.5 rounded-full bg-up' />Live{now !== null ? `, ${formatEatClock(now)} EAT` : ''}</span>
                            : `Closing price, ${formatDay(sessionDay, 'long')}`}
                    </p>
                </div>
            </div>

            <dl className='mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-4 xl:grid-cols-8'>
                {stats.map(item => (
                    <div key={item.label} className='bg-card px-4 py-3'>
                        <dt className='text-[11px] text-muted-foreground'>{item.label}</dt>
                        <dd className='mt-0.5 text-sm font-semibold tabular-nums text-foreground'>{item.value}</dd>
                    </div>
                ))}
            </dl>
        </header>
    )
}

export default StockHeader
